import React from 'react';
import { BathFixtureShell, Circle, Rect } from './BathFixtureFrame';
import { Bar } from './ShowerSchematicPrimitives';
import {
  resolveShowerControlsVariant,
  showerControlsLayout,
} from '../../3d/geometry/proceduralRenderers/showerLayout';

/**
 * Shower-controls elevation — an orthographic projection of the SAME
 * parts the 3D renderer builds (`showerLayout.js` decides both). Variants:
 *   - single-handle (default; `single-lever` maps here): one escutcheon
 *     (round in a square-ish box, rectangular trim otherwise) with a
 *     lever in the off position.
 *   - two-handle: hot / cold escutcheons with cross handles.
 *   - thermostatic: one rectangular plate with two knobs.
 *
 * Front view (width × height) is the plate face; side views (depth ×
 * height) show the plate thickness against the wall and how far the
 * handles stand off it. Wall at the left edge of a left view; the right
 * view mirrors it.
 */

export { resolveShowerControlsVariant };

export function ShowerControlsSchematic({
  widthMeters,
  heightMeters,
  depthMeters,
  obj = {},
  strokeColor,
  strokeWidth,
  view = 'front',
}) {
  const dims = { width: widthMeters, height: heightMeters, depth: depthMeters };
  const L = showerControlsLayout(dims, resolveShowerControlsVariant(obj));
  const stroke = { strokeColor, strokeWidth };
  if (view === 'left' || view === 'right') {
    const map = ([z, y]) => [z + depthMeters / 2, heightMeters / 2 - y];
    return (
      <BathFixtureShell width={depthMeters} height={heightMeters}>
        <g
          transform={
            view === 'right'
              ? `translate(${depthMeters}, 0) scale(-1, 1)`
              : undefined
          }
        >
          {L.plates.map((p, i) => (
            <PlateSide
              key={`p${i}`}
              plate={p}
              L={L}
              map={map}
              stroke={stroke}
            />
          ))}
          {L.handles.map((h, i) => (
            <HandleSide key={`h${i}`} h={h} map={map} stroke={stroke} />
          ))}
        </g>
      </BathFixtureShell>
    );
  }
  const map = ([x, y]) => [x + widthMeters / 2, heightMeters / 2 - y];
  return (
    <BathFixtureShell width={widthMeters} height={heightMeters}>
      {L.plates.map((p, i) => (
        <PlateFront key={`p${i}`} plate={p} map={map} stroke={stroke} />
      ))}
      {L.handles.map((h, i) => (
        <HandleFront key={`h${i}`} h={h} map={map} stroke={stroke} />
      ))}
    </BathFixtureShell>
  );
}

ShowerControlsSchematic.supportsSideViews = true;

// ---------------------------------------------------------------------------
// Front view (x, y)
// ---------------------------------------------------------------------------

function PlateFront({ plate, map, stroke }) {
  if (plate.shape === 'round') {
    const [cx, cy] = map([plate.x, plate.y]);
    return <Circle cx={cx} cy={cy} r={plate.r} {...stroke} />;
  }
  const [x, y] = map([plate.x - plate.w / 2, plate.y + plate.h / 2]);
  return (
    <Rect
      x={x}
      y={y}
      width={plate.w}
      height={plate.h}
      rx={plate.rx}
      ry={plate.rx}
      {...stroke}
    />
  );
}

function HandleFront({ h, map, stroke }) {
  const [cx, cy] = map([h.x, h.y]);
  if (h.kind === 'lever') {
    const { hub, lever } = h;
    const tip = [
      h.x + Math.cos(lever.angle) * lever.len,
      h.y + Math.sin(lever.angle) * lever.len,
    ];
    const [tx, ty] = map(tip);
    return (
      <g>
        <Circle cx={cx} cy={cy} r={hub.r} {...stroke} />
        <Bar from={[h.x, h.y]} to={tip} r={lever.r} map={map} {...stroke} />
        <Circle cx={tx} cy={ty} r={lever.r} {...stroke} />
      </g>
    );
  }
  if (h.kind === 'cross') {
    const { hub, cross } = h;
    const half = cross.len / 2;
    return (
      <g>
        <Circle cx={cx} cy={cy} r={hub.r} {...stroke} />
        <Bar
          from={[h.x - half, h.y]}
          to={[h.x + half, h.y]}
          r={cross.r}
          map={map}
          {...stroke}
        />
        <Bar
          from={[h.x, h.y - half]}
          to={[h.x, h.y + half]}
          r={cross.r}
          map={map}
          {...stroke}
        />
      </g>
    );
  }
  return (
    <g>
      <Circle cx={cx} cy={cy} r={h.knob.r} {...stroke} />
      <Circle cx={cx} cy={cy} r={h.knob.r * 0.85} {...stroke} />
    </g>
  );
}

// ---------------------------------------------------------------------------
// Side view (z, y) — wall at z = wallZ
// ---------------------------------------------------------------------------

function PlateSide({ plate, L, map, stroke }) {
  const halfH = plate.shape === 'round' ? plate.r : plate.h / 2;
  const [x, y] = map([L.wallZ, plate.y + halfH]);
  return <Rect x={x} y={y} width={plate.t} height={halfH * 2} {...stroke} />;
}

/** Axis-aligned stub along z (hub, stem, knob) at height y. */
function StubSide({ z0, z1, y, r, map, stroke }) {
  return <Bar from={[z0, y]} to={[z1, y]} r={r} map={map} {...stroke} />;
}

function HandleSide({ h, map, stroke }) {
  if (h.kind === 'lever') {
    const { hub, stem, lever } = h;
    const tipY = h.y + Math.sin(lever.angle) * lever.len;
    const [bx, by] = map([lever.z, h.y]);
    const [, ty] = map([lever.z, tipY]);
    return (
      <g>
        <StubSide
          z0={hub.z0}
          z1={hub.z1}
          y={h.y}
          r={hub.r}
          map={map}
          stroke={stroke}
        />
        <StubSide
          z0={stem.z0}
          z1={stem.z1}
          y={h.y}
          r={stem.r}
          map={map}
          stroke={stroke}
        />
        <Bar
          from={[lever.z, h.y]}
          to={[lever.z, tipY]}
          r={lever.r}
          map={map}
          {...stroke}
        />
        <Circle cx={bx} cy={by} r={lever.r} {...stroke} />
        <Circle cx={bx} cy={ty} r={lever.r} {...stroke} />
      </g>
    );
  }
  if (h.kind === 'cross') {
    const { hub, stem, cross } = h;
    const half = cross.len / 2;
    return (
      <g>
        <StubSide
          z0={hub.z0}
          z1={hub.z1}
          y={h.y}
          r={hub.r}
          map={map}
          stroke={stroke}
        />
        <StubSide
          z0={stem.z0}
          z1={stem.z1}
          y={h.y}
          r={stem.r}
          map={map}
          stroke={stroke}
        />
        <Bar
          from={[cross.z, h.y - half]}
          to={[cross.z, h.y + half]}
          r={cross.r}
          map={map}
          {...stroke}
        />
      </g>
    );
  }
  const { knob } = h;
  return (
    <g>
      <StubSide
        z0={knob.z0}
        z1={knob.z1}
        y={h.y}
        r={knob.r}
        map={map}
        stroke={stroke}
      />
      <StubSide
        z0={knob.z1 - knob.faceT}
        z1={knob.z1}
        y={h.y}
        r={knob.r * 0.85}
        map={map}
        stroke={stroke}
      />
    </g>
  );
}
